import type { ServiceOption } from './servicesStore';

type PaymentProviderKind = 'payment' | 'order';

interface PaymentCustomer {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface PaymentSession {
  token: string;
  providerId: string;
  providerType: PaymentProviderKind;
  serviceId?: string;
  serviceName?: string;
  paymentType?: 'full' | 'klarna';
  amount?: number;
  currencyCode?: string;
  selectedOptions?: ServiceOption[];
  company?: string;
  customer?: PaymentCustomer;
  createdAt?: string;
}

class PaymentSessionService {
  private readonly storageKey = 'mollie_payment_sessions';

  private readSessions(): Record<string, PaymentSession> {
    try {
      const rawValue = localStorage.getItem(this.storageKey);
      if (!rawValue) {
        return {};
      }

      const parsed = JSON.parse(rawValue);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private writeSessions(sessions: Record<string, PaymentSession>) {
    localStorage.setItem(this.storageKey, JSON.stringify(sessions));
  }

  public saveSession(session: PaymentSession) {
    const sessions = this.readSessions();
    sessions[session.token] = session;
    this.writeSessions(sessions);
  }

  public getSession(token: string): PaymentSession | null {
    const sessions = this.readSessions();
    return sessions[token] || null;
  }

  public clearSession(token: string) {
    const sessions = this.readSessions();
    if (!sessions[token]) {
      return;
    }

    delete sessions[token];
    this.writeSessions(sessions);
  }
}

export const paymentSessionService = new PaymentSessionService();
